import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { computeFloatingPosition, VwPlacement } from '../floating-position.util';

// Rendered dynamically (see VwTooltipDirective) and appended to <body>, so it
// can float above everything and be positioned with viewport-relative
// coordinates regardless of where its anchor lives in the layout.
//
// reposition() is intentionally NOT called from ngAfterViewInit: mutating
// the bound top/left/resolvedPlacement there re-enters the very change
// detection pass that just rendered them and trips
// ExpressionChangedAfterItHasBeenCheckedError in dev mode. The directive
// calls reposition() itself via setTimeout right after creating this
// component, which runs as its own macrotask instead.
@Component({
  selector: 'app-vw-tooltip',
  templateUrl: './vw-tooltip.component.html',
  styleUrls: ['./vw-tooltip.component.scss'],
})
export class VwTooltipComponent {
  @Input() title: string;
  @Input() body: string;
  @Input() image: string;
  @Input() linkText: string;
  @Input() linkUrl: string;
  @Input() placement: VwPlacement = 'auto';
  // false for plain reference/info tooltips (e.g. sidebar nav hints) that
  // should just disappear on mouseleave — the close button and "don't show
  // again" link only make sense for the persisted, dismiss-once-and-forget
  // onboarding tips, so they're hidden entirely otherwise.
  @Input() dismissible = true;

  // Set by the directive right after creation — the element the tooltip floats next to.
  anchor: HTMLElement;

  @Output() dismiss = new EventEmitter<void>();
  @Output() dontShowAgain = new EventEmitter<void>();

  @ViewChild('panel', { static: false }) panelRef: ElementRef<HTMLElement>;

  top = 0;
  left = 0;
  resolvedPlacement: Exclude<VwPlacement, 'auto'> = 'bottom';

  reposition(): void {
    if (!this.anchor || !this.panelRef) {
      return;
    }
    const anchorRect = this.anchor.getBoundingClientRect();
    const panel = this.panelRef.nativeElement;
    const result = computeFloatingPosition(anchorRect, panel.offsetWidth || 280, panel.offsetHeight || 140, this.placement);
    this.top = result.top;
    this.left = result.left;
    this.resolvedPlacement = result.placement;
  }

  onClose(): void {
    this.dismiss.emit();
  }

  onDontShowAgain(): void {
    this.dontShowAgain.emit();
  }
}
